/**
 * BUSCA, ORDENAÇÃO, RECORTE e RESUMO da lista de NOTAS FISCAIS (`/fiscal`).
 *
 * Vive fora da página pelo mesmo motivo do `quoteListFilters`, do
 * `serviceOrdersListFilters` e do `receiptsListFilters`: são regras que decidem
 * QUAIS notas o prestador vê e em que ordem, e todas erram em silêncio — uma
 * lista recortada errado não dá erro, só esconde documento fiscal.
 *
 * ─── O QUE É ESPECÍFICO DESTA LISTA ─────────────────────────────────────────
 *
 * · **DOZE STATUS NÃO CABEM EM DOZE BOTÕES — o recorte é por FAMÍLIA.** A
 *   máquina de estados da NFS-e tem 12 posições (`NFSE_STATUS_META`), e uma
 *   faixa com 12 pílulas seria uma barra que rola sem fim, com botões que
 *   quase sempre trariam zero. Os botões agrupam por PERGUNTA — "saiu?", "está
 *   andando?", "morreu?", "preciso agir?" — e o selo do card continua dizendo o
 *   status exato, que é onde a precisão importa. O mapa é EXAUSTIVO
 *   (`Record<NfseStatus, …>`): status novo no schema quebra o `tsc` aqui até
 *   alguém decidir a que família ele pertence, em vez de sumir do recorte.
 *
 * · **O DINHEIRO MORTO SÃO DOIS: `rejected` e `cancelled`.** Rejeitada é nota
 *   que nunca existiu; cancelada é nota anulada. Somar as duas diria ao
 *   prestador que ele faturou mais do que faturou (o corolário do #182/#188), e
 *   quantas ficaram fora é DEVOLVIDO para a tela dizer ("no silent caps", #61).
 *   `cancellation_denied` NÃO é morta, e isso é decisão, não descuido: o
 *   cancelamento foi NEGADO, então a nota continua válida e continua valendo
 *   dinheiro — tratá-la como cancelada seria o app contradizendo o fisco. As em
 *   curso (rascunho, fila, processando) também contam: elas ainda podem virar
 *   nota, e o recorte "Autorizadas" é quem dá o número estrito de quem já saiu.
 *
 * · **A data do recorte é `created_at` (TIMESTAMP)**, o mesmo contrato de
 *   `/quotes` e `/ordens-servico`. `competencia` existe e é outra coisa (o mês
 *   de referência fiscal): recortar por ela faria uma nota sem competência
 *   sumir do filtro sem explicação nenhuma.
 *
 * O parser de dinheiro é o `quoteTotalNumber` (#123a) — `valor_servico` chega
 * como `number` e como STRING do PostgREST (`numeric`) — e a normalização de
 * texto dobra caixa E acento (#190d), senão quem digita "conceicao" no teclado
 * do celular recebe zero por "Conceição".
 */
import { normalizeSearchText } from '@/lib/quoteSearch';
import { quoteTotalNumber } from '@/lib/quotesListSummary';
import { matchesListRange, type ListValueRange } from '@/lib/listFilters';
import type { ReportDateRange, ReportPeriodPreset } from '@/lib/reportPeriod';
import type { NfseStatus } from '@/types/fiscal';

/**
 * O que este módulo enxerga de uma nota.
 *
 * Os dois snapshots são `jsonb` (chegam como `unknown`), então tudo aqui é lido
 * com cuidado — nada é `as` sobre linha de banco (#168a).
 */
export interface NfseRow {
  id?: string | null;
  numero?: string | null;
  status?: string | null;
  valor_servico?: unknown;
  created_at?: string | null;
  tomador_snapshot?: unknown;
  servico_snapshot?: unknown;
}

/** O nome do tomador, lido do snapshot sem `as`. */
export function nfseTomadorNome(nfse: NfseRow): string {
  const snapshot = nfse?.tomador_snapshot;
  if (!snapshot || typeof snapshot !== 'object') return '';
  const nome = (snapshot as { nome?: unknown }).nome;
  return typeof nome === 'string' ? nome : '';
}

/** A descrição do serviço, lida do snapshot sem `as`. */
export function nfseDescricao(nfse: NfseRow): string {
  const snapshot = nfse?.servico_snapshot;
  if (!snapshot || typeof snapshot !== 'object') return '';
  const descricao = (snapshot as { descricao?: unknown }).descricao;
  return typeof descricao === 'string' ? descricao : '';
}

// ─── Busca ───────────────────────────────────────────────────────────────────

/**
 * Termo vazio casa com tudo (é o estado sem filtro).
 *
 * Campos varridos: tomador, descrição do serviço e NÚMERO da nota — exatamente
 * o que o card mostra.
 *
 * O número da NFS-e é diferente do `#0001` dos outros módulos: ele é atribuído
 * pelo FISCO, é texto (pode ter zeros à esquerda e tamanho variável por
 * município) e não existe enquanto a nota não é autorizada. Por isso aqui ele é
 * casado como TEXTO por `includes` — comparar como número perderia o zero à
 * esquerda que o prestador tem impresso no papel na frente dele.
 */
export function nfseMatchesSearch(nfse: NfseRow, term: string): boolean {
  const needle = normalizeSearchText(term);
  if (!needle) return true;

  const numero = normalizeSearchText(nfse?.numero);
  // O `#` é como as pessoas escrevem "número"; ele não faz parte do valor.
  const semCerquilha = needle.replace(/^#/, '');
  if (semCerquilha && numero.includes(semCerquilha)) return true;

  if (normalizeSearchText(nfseTomadorNome(nfse)).includes(needle)) return true;
  if (normalizeSearchText(nfseDescricao(nfse)).includes(needle)) return true;
  return false;
}

// ─── Ordenação ───────────────────────────────────────────────────────────────

export type NfseSortOption = 'recent' | 'oldest' | 'highest' | 'lowest' | 'tomador';

export const NFSE_SORT_LABELS: Record<NfseSortOption, string> = {
  recent: 'Mais recentes',
  oldest: 'Mais antigas',
  highest: 'Maior valor',
  lowest: 'Menor valor',
  tomador: 'Nome do tomador',
};

export const NFSE_SORT_ORDER: readonly NfseSortOption[] = [
  'recent',
  'oldest',
  'highest',
  'lowest',
  'tomador',
];

/**
 * `recent` é o PADRÃO porque é como a lista sempre se comportou (a consulta já
 * devolve `created_at desc`). Trocar o padrão junto com o redesenho mudaria a
 * tela de todo mundo sem ninguém ter pedido.
 */
export const DEFAULT_NFSE_SORT: NfseSortOption = 'recent';

/**
 * Data ilegível vai para o FIM em "mais recentes" (e não para o topo, que é
 * onde `NaN → 0` a colocaria em "mais antigas").
 */
function timeOf(nfse: NfseRow): number {
  const parsed = new Date(nfse?.created_at ?? '').getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Vazio vai para o FIM (`￿`): um punhado de "sem tomador" no topo esconde o resto. */
function textOf(value: unknown): string {
  return normalizeSearchText(value) || '￿';
}

function valorOf(nfse: NfseRow): number {
  return quoteTotalNumber(nfse?.valor_servico);
}

/** Não muta a entrada: a lista da tela é derivada da consulta. */
export function sortNfseBy<T extends NfseRow>(notes: readonly T[], option: NfseSortOption): T[] {
  const rows = [...notes];
  // Desempate ESTÁVEL por id (#93): sem ele, duas notas do mesmo instante (uma
  // emissão em lote) trocam de lugar entre renders e a lista "pisca".
  const byId = (a: T, b: T) => String(a?.id).localeCompare(String(b?.id));

  switch (option) {
    case 'oldest':
      return rows.sort((a, b) => timeOf(a) - timeOf(b) || byId(a, b));
    case 'highest':
      return rows.sort((a, b) => valorOf(b) - valorOf(a) || byId(a, b));
    case 'lowest':
      return rows.sort((a, b) => valorOf(a) - valorOf(b) || byId(a, b));
    case 'tomador':
      return rows.sort(
        (a, b) =>
          textOf(nfseTomadorNome(a)).localeCompare(textOf(nfseTomadorNome(b))) || byId(a, b),
      );
    case 'recent':
    default:
      return rows.sort((a, b) => timeOf(b) - timeOf(a) || byId(a, b));
  }
}

// ─── Recorte por FAMÍLIA de status (os botões da faixa) ─────────────────────

export type NfseStatusFamily = 'authorized' | 'progress' | 'cancelled' | 'attention';
export type NfseStatusFilter = 'all' | NfseStatusFamily;

/**
 * De qual FAMÍLIA cada status faz parte.
 *
 * EXAUSTIVO de propósito (o mesmo contrato do `NFSE_STATUS_META`): status novo
 * no schema quebra o `tsc` aqui até alguém classificá-lo. Sem isso ele cairia
 * fora de todos os botões e a nota ficaria invisível em qualquer recorte que
 * não fosse "Todas" — sem erro nenhum.
 *
 * `cancellation_queued`/`cancellation_processing` são PROGRESSO, não
 * "canceladas": o cancelamento foi pedido e ainda não aconteceu, e listá-las
 * como canceladas diria que a nota já morreu quando ela ainda vale.
 * `cancellation_denied` é ATENÇÃO: o fisco recusou o cancelamento, e é uma
 * situação que o prestador precisa resolver — mas a nota segue válida.
 */
export const NFSE_STATUS_FAMILY: Record<NfseStatus, NfseStatusFamily> = {
  draft: 'progress',
  ready: 'progress',
  queued: 'progress',
  submitting: 'progress',
  processing_provider: 'progress',
  authorized: 'authorized',
  rejected: 'attention',
  unknown_reconciliation_required: 'attention',
  cancellation_queued: 'progress',
  cancellation_processing: 'progress',
  cancelled: 'cancelled',
  cancellation_denied: 'attention',
};

/** Status desconhecido (linha de cache antiga) conta como ATENÇÃO — nunca some. */
export function nfseStatusFamily(nfse: NfseRow): NfseStatusFamily {
  const status = String(nfse?.status ?? '') as NfseStatus;
  return NFSE_STATUS_FAMILY[status] ?? 'attention';
}

/**
 * Os botões da faixa, na ordem do ciclo de vida da nota.
 *
 * ─── OS RÓTULOS SÃO MEDIDOS, NÃO ESCOLHIDOS ────────────────────────────────
 *
 * Em 360px a faixa tem 336px úteis, e os cinco rótulos custam (medido por
 * réplica de DOM com a fonte real, chips `text-[11px]` + `px-1.5`, gaps
 * incluídos):
 *
 *   Todas · Autoriz.    · Em curso     · Cancel.    · Atenção    → 284  ✓ (+52)
 *   Todas · Autorizadas · Em curso     · Canceladas · Atenção    → 324  ✓ (+12)
 *   Todas · Autorizadas · Em curso     · Canceladas · Problemas  → 336  ✓ (±0)
 *   Todas · Autorizadas · Em curso     · Canceladas · Pendências → 339  ✗
 *   Todas · Autorizadas · Em andamento · Canceladas · Atenção    → 354  ✗
 *
 * A primeira linha é a versão abreviada, e ela NÃO foi a escolhida: o #191 diz
 * para medir a escada inteira antes de negociar o enunciado, e aqui a palavra
 * inteira COUBE. Abreviar sem precisar inventaria uma segunda palavra para o
 * mesmo estado — e o selo do próprio card, uma linha abaixo, diz "Autorizada" e
 * "Cancelada". "Problemas" também cabe, mas com ZERO folga: uma métrica de
 * fonte diferente empurraria o último botão para fora da viewport (#185), e por
 * isso ficou "Atenção", com 12px de sobra.
 *
 * `srLabel` é o nome COMPLETO do recorte e vai para o `aria-label`/`title`,
 * onde não custa pixel nenhum (#142/#191). Ele só difere do `label` onde a
 * palavra sozinha não se explica: "Atenção" ouvido por um leitor de tela não
 * diz o que aquele botão traz.
 */
export const NFSE_STATUS_FILTER_OPTIONS: readonly {
  value: NfseStatusFilter;
  label: string;
  srLabel: string;
}[] = [
  { value: 'all', label: 'Todas', srLabel: 'Todas' },
  { value: 'authorized', label: 'Autorizadas', srLabel: 'Autorizadas' },
  { value: 'progress', label: 'Em curso', srLabel: 'Em curso' },
  { value: 'cancelled', label: 'Canceladas', srLabel: 'Canceladas' },
  { value: 'attention', label: 'Atenção', srLabel: 'Precisam de atenção' },
];

export function matchesNfseStatus(nfse: NfseRow, filter: NfseStatusFilter): boolean {
  if (filter === 'all') return true;
  return nfseStatusFamily(nfse) === filter;
}

/**
 * Quantas notas cada botão traria — contadas sobre o recorte JÁ filtrado por
 * busca, período e valor, mas ANTES do status (#193/#196).
 */
export function countNfseByStatus(notes: readonly NfseRow[]): Record<NfseStatusFilter, number> {
  const counts: Record<NfseStatusFilter, number> = {
    all: notes.length,
    authorized: 0,
    progress: 0,
    cancelled: 0,
    attention: 0,
  };
  for (const nfse of notes) counts[nfseStatusFamily(nfse)] += 1;
  return counts;
}

// ─── Período e faixa de valor (o modal) ─────────────────────────────────────

/** Mesmo subconjunto explícito de `/quotes`, `/recibos` e `/ordens-servico`. */
export const NFSE_PERIOD_PRESETS: readonly ReportPeriodPreset[] = [
  'today',
  'thisWeek',
  'thisMonth',
  '30d',
  '3m',
  'thisYear',
];

/** Nota SEM data legível NÃO é recortada (#193-d). */
export function matchesNfsePeriod(nfse: NfseRow, range: ReportDateRange | null): boolean {
  if (!range) return true;
  const time = new Date(nfse?.created_at ?? '').getTime();
  if (!Number.isFinite(time)) return true;
  return time >= range.from.getTime() && time <= range.to.getTime();
}

/** `null` em `min`/`max` é SEM LIMITE, nunca zero (ver `listFilters`). */
export function matchesNfseValue(nfse: NfseRow, range: ListValueRange): boolean {
  return matchesListRange(nfse?.valor_servico, range);
}

/** Quantos filtros do MODAL estão ativos — vira o selo no ícone. */
export function countActiveNfseFilters(
  period: ReportPeriodPreset | null,
  range: ListValueRange,
): number {
  let active = 0;
  if (period) active += 1;
  if (range.min !== null || range.max !== null) active += 1;
  return active;
}

// ─── Resumo do hero ─────────────────────────────────────────────────────────

/** As famílias que NÃO representam dinheiro faturado (ver o cabeçalho). */
export const DEAD_NFSE_STATUSES: ReadonlySet<string> = new Set<NfseStatus>([
  'rejected',
  'cancelled',
]);

/**
 * `true` quando o recorte é um recorte de dinheiro morto.
 *
 * "Canceladas" é a família morta inteira; "Atenção" contém as rejeitadas mas
 * também as que continuam valendo (`cancellation_denied`), então ele NÃO liga o
 * `includeDead` — ali a exclusão continua sendo a resposta certa, com a
 * contagem do que ficou de fora escrita na sublinha.
 */
export function isDeadNfseStatusFilter(filter: NfseStatusFilter): boolean {
  return filter === 'cancelled';
}

export interface NfseListSummary {
  /** Notas no recorte — todas, inclusive as rejeitadas/canceladas. */
  count: number;
  /** Soma das que ainda representam faturamento. */
  total: number;
  /** Quantas ficaram FORA da soma. Nunca cortado em silêncio (#61). */
  excludedCount: number;
}

export interface SummarizeNfseOptions {
  /** Soma TAMBÉM as mortas — ver `isDeadNfseStatusFilter`. */
  includeDead?: boolean;
}

/** Resumo do recorte JÁ FILTRADO (busca, status, período e valor aplicados). */
export function summarizeNfseList(
  notes: readonly NfseRow[],
  options: SummarizeNfseOptions = {},
): NfseListSummary {
  let total = 0;
  let excludedCount = 0;

  for (const nfse of notes) {
    if (!options.includeDead && DEAD_NFSE_STATUSES.has(String(nfse?.status))) {
      excludedCount += 1;
      continue;
    }
    total += valorOf(nfse);
  }

  return { count: notes.length, total, excludedCount };
}

/**
 * O RÓTULO do valor do hero — é ele que torna o número honesto (#182/#188).
 */
export function nfseListTotalLabel(filter: NfseStatusFilter): string {
  switch (filter) {
    case 'authorized':
      return 'Total autorizado';
    case 'progress':
      return 'Total em curso';
    case 'cancelled':
      return 'Total cancelado';
    case 'attention':
      return 'Total a resolver';
    default:
      return 'Total em notas fiscais';
  }
}
